import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { Controller, useForm } from 'react-hook-form';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import BasicModal from '../../../components/BasicModal';
import MyAppText from '../../../components/MyAppText';
import MyDropDownPicker from '../../../components/MyDropDownPicker';
import { translate } from '../../../services/translate';
import OseFieldLabel from './OseFieldLabel';
import FontWeight from '../../../styles/fontWeights';

export const FUEL_LEVELS = [5, 25, 50, 75, 100];

export interface CreateOseForm {
    driverId: number | null;
    personInChargeId: number | null;
    startFuelLevel: number | null;
}

interface Props {
    team: AppAgentUnit | null;
    visible: boolean;
    setVisible: (value: boolean) => void;
    onSubmit: (data: CreateOseForm) => Promise<boolean>;
    isSubmitting: boolean;
}

export default function CreateOseModal({ team, visible, setVisible, onSubmit, isSubmitting }: Props) {
    const { styles } = useStyles(styleSheet);
    const [driverOpen, setDriverOpen] = useState(false);
    const [personOpen, setPersonOpen] = useState(false);

    const keepOpenRef = useRef(false);

    const needsStartFuel = !!team?.unitName.unitCategory?.startFuelLevel;

    const members = useMemo(() => {
        return (team?.UnitUsers ?? [])
            .map((user) => ({ user, active: !!user.unitId && user.unitId === team?.id }))
            .sort((a, b) => Number(b.active) - Number(a.active) || a.user.name.localeCompare(b.user.name));
    }, [team]);

    function getUserName(user: UserAgent) {
        return user.warName ?? user.name;
    }

    const items = useMemo(() => members.map(({ user, active }) => ({
        label: active ? getUserName(user) : `${getUserName(user)} (${translate('inactive')})`,
        value: user.id,
    })), [members]);

    const defaults = useMemo<CreateOseForm>(() => ({
        driverId: members.find((m) => m.user.UnitUser?.isDriver)?.user.id ?? null,
        personInChargeId: members.find((m) => m.user.UnitUser?.isPersonInCharge)?.user.id ?? null,
        startFuelLevel: null,
    }), [members]);

    const form = useForm<CreateOseForm>({ defaultValues: defaults, mode: 'onChange' });

    useEffect(() => {
        if (visible) {
            form.reset(defaults);
            keepOpenRef.current = false;
            setDriverOpen(false);
            setPersonOpen(false);
        }
    }, [visible, team?.id]);

    function handleSetVisible(value: boolean) {
        if (!value && keepOpenRef.current) {
            keepOpenRef.current = false;
            return;
        }
        setVisible(value);
    }

    return (
        <BasicModal
            isModalVisible={visible}
            setModalVisible={handleSetVisible}
            width='small'
            mobileHeight='fill'
            title={translate('newOse')}
            disableScrollView
            primaryButton={{
                text: translate('createOse'),
                disabled: !form.formState.isValid || isSubmitting,
                onClick: async () => {
                    let ok = false;
                    try {
                        ok = await onSubmit(form.getValues());
                    } catch {
                        ok = false;
                    }
                    keepOpenRef.current = !ok;
                },
            }}
        >
            <>
                <View style={[styles.field, { zIndex: 3 }]}>
                    <OseFieldLabel label={translate('driver')} labelStyle={styles.label} required />
                    <Controller
                        control={form.control}
                        name='driverId'
                        rules={{ required: true }}
                        render={({ field }) => (
                            <MyDropDownPicker
                                open={driverOpen}
                                setOpen={(open) => {
                                    setDriverOpen(open);
                                    setPersonOpen(false);
                                }}
                                value={field.value}
                                setValue={(cb) => field.onChange(cb(field.value))}
                                items={items}
                                dropDownDirection='BOTTOM'
                                searchable={true}
                            />
                        )}
                    />
                </View>

                <View style={[styles.field, { zIndex: 2 }]}>
                    <OseFieldLabel label={translate('personInCharge')} labelStyle={styles.label} required />
                    <Controller
                        control={form.control}
                        name='personInChargeId'
                        rules={{ required: true }}
                        render={({ field }) => (
                            <MyDropDownPicker
                                open={personOpen}
                                setOpen={(open) => {
                                    setPersonOpen(open);
                                    setDriverOpen(false);
                                }}
                                value={field.value}
                                setValue={(cb) => field.onChange(cb(field.value))}
                                items={items}
                                dropDownDirection='BOTTOM'
                                searchable={true}
                            />
                        )}
                    />
                </View>

                {needsStartFuel && (
                    <View style={[styles.field, { zIndex: 1 }]}>
                        <OseFieldLabel label={translate('startFuelLevel')} labelStyle={styles.label} required />
                        <Controller
                            control={form.control}
                            name='startFuelLevel'
                            rules={{ required: true }}
                            render={({ field }) => (
                                <View style={styles.chipsRow}>
                                    {FUEL_LEVELS.map((level) => (
                                        <TouchableOpacity
                                            key={level}
                                            style={[styles.chip, field.value === level && styles.chipSelected]}
                                            onPress={() => field.onChange(level)}
                                        >
                                            <MyAppText style={[styles.chipText, field.value === level && styles.chipTextSelected]}>
                                                {`${level}%`}
                                            </MyAppText>
                                        </TouchableOpacity>
                                    ))}
                                </View>
                            )}
                        />
                    </View>
                )}
            </>
        </BasicModal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    field: { gap: 6 },
    label: {
        fontSize: 14,
        fontWeight: FontWeight.semiBold,
        color: theme.colors.neutral['500']
    },
    chipsRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8
    },
    chip: {
        paddingVertical: 8,
        paddingHorizontal: 14,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
    },
    chipSelected: {
        backgroundColor: theme.colors.primary['500'],
        borderColor: theme.colors.primary['500'],
    },
    chipText: {
        fontSize: 14,
        color: theme.colors.neutral['500']
    },
    chipTextSelected: {
        color: theme.colors.white['500'],
        fontWeight: FontWeight.semiBold
    },
    error: {
        fontSize: 13,
        color: theme.colors.errors[300]
    },
}));
